import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import isEqual from 'react-fast-compare';
import type { DependsOnType } from '@unifyapps/defs/types/page';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import type { PageVariable } from '@unifyapps/defs/types/pageVariable';
import type { BlockControlMethod } from '../../components/BlockDefinition';
import { useRegistryContext } from '../../components/RegistryProvider/context';
import { useActionsContext } from '../../components/ActionsProvider';
import {
  getActivePage,
  getBlocksFromPage,
  useGetInterfaceStoreState,
  useInterfaceStore,
} from '../../stores/InterfaceStore';
import { useBlockRuntimeData } from '../../components/BlockRuntimeStateProvider';
import type { GlobalStateStoreType } from '../../stores/GlobalStateStore';
import { useGetGlobalStateStoreState, useGlobalStateStore } from '../../stores/GlobalStateStore';
import { useDependencyGraphProvider } from '../../context/DependencyGraphContext';
import { useGetSubscribeToDependencyUpdates } from '../../context/DependencyGraphContext/hooks/useGetSubscribeToDependencyUpdates';
import { useGetDataSourceRecordStoreState } from '../../stores/DataSourceRecordStore';
import { EMPTY_OBJECT } from '../../../consts/empty';
import { getIsDependentContextChanged } from './utils/getIsDependentContextChanged';
import { useGetNoCodeComputedData } from './useGetNoCodeComputedData';
import type { ComputeContextType } from './BuildComputeContext';
import BuildComputeContext from './BuildComputeContext';
import { getBlocksMethods, getDataSourcesMethods, getPageVariablesMethods } from './utils/methods';

export type GetComputeContextBuilder = () => ComputeContextType;

/**
 * Defines the mode of operation for the compute context.
 *
 * - 'readonly': Does not populate methods for blocks, page variables, and page functions,
 *   preventing mutation of the global state store. This is the default and preferred mode
 *   for most use cases as it improves performance by avoiding unnecessary function creation.
 *
 * - 'mutable': Creates methods for all entities (blocks, page variables, page functions),
 *   allowing them to mutate the global state store. This mode should only be used when
 *   state mutations are explicitly required.
 *
 * Using 'readonly' mode provides significant performance benefits by reducing function
 * creation and reference management overhead when creating compute contexts.
 */
export type ComputeContextMode = 'readonly' | 'mutable';

const buildComputeContextFromState = ({
  state,
  blocksRuntimeState,
  interfaceRecord,
  blockMethods,
  dataSourceMethods,
  pageVariableMethods,
  // walkthrough,
}: {
  state: Pick<
    GlobalStateStoreType,
    | 'blocks'
    | 'blockRefs'
    | 'dataSources'
    | 'dataSourcesMeta'
    | 'pageInputs'
    | 'location'
    | 'userContext'
    | 'pageVariables'
    | 'pageFunctions'
    | 'interfaceSessionStorage'
    | 'envVariables'
  >;
  interfaceRecord: InterfaceEntity;
  blocksRuntimeState?: Record<string, Record<string, unknown>>;
  blockMethods: Record<string, BlockControlMethod[] | undefined>;
  dataSourceMethods: Record<string, unknown>;
  pageVariableMethods: Record<string, unknown>;
  // walkthrough: Record<string, unknown>;
}) => {
  const buildComputeContext = new BuildComputeContext();

  buildComputeContext.setBlockStates(state.blocks);
  buildComputeContext.setBlockRefs(state.blockRefs);
  buildComputeContext.setMethods({
    blockMethods,
    dataSourceMethods,
    pageVariableMethods,
  });
  buildComputeContext.setDataSources(state.dataSources);
  buildComputeContext.setDataSourcesMeta(state.dataSourcesMeta);
  buildComputeContext.setPageInputs(state.pageInputs);
  buildComputeContext.setLocation(state.location);
  buildComputeContext.setUserContext(state.userContext);
  buildComputeContext.setPageVariables(state.pageVariables);
  buildComputeContext.setPageFunctions(state.pageFunctions);
  buildComputeContext.setInterfaceSessionStorage(state.interfaceSessionStorage);
  buildComputeContext.setInterfaceRecord(interfaceRecord);
  buildComputeContext.setBlocksRuntimeState(blocksRuntimeState);
  // buildComputeContext.setWalkthrough(walkthrough);
  buildComputeContext.setEnvVariables(state.envVariables);

  return buildComputeContext.build();
};

/**
 * This hook is used to get the methods associated with each interface entity, which helps us to change their state from a script
 */
function useInterfaceEntityMethods({ mode }: { mode: ComputeContextMode }) {
  const { getGlobalStateStoreState } = useGetGlobalStateStoreState();
  const { getInterfaceStoreState } = useGetInterfaceStoreState();
  const { getDataSourceRecordStoreState } = useGetDataSourceRecordStoreState();
  const { registry } = useRegistryContext();
  const { doAction: onAction } = useActionsContext();
  const { device } = useGlobalStateStore().use.deviceDetails();

  // We need to keep this outside the useMemo because, we want to recompute block methods everytime the hook is called,
  const blockStates = getGlobalStateStoreState().blocks;

  // Since we only need recalculate the block methods when the block is added or removed (mainly runtime blocks), we are using the blockIdMap as a dependency in useMemo
  // in getBlockMethods we are only using componentType which will remain same for the same block
  const blockIdsFingerprint = useMemo(() => Object.keys(blockStates).join(''), [blockStates]);
  const blockMethods = useMemo(() => {
    if (mode === 'readonly') return {};

    const blocks = getBlocksFromPage(device, getInterfaceStoreState());
    return getBlocksMethods({
      blocks,
      blockStates,
      onAction,
      blockRegistry: registry,
    });
    // eslint-disable-next-line react-compiler/react-compiler -- needed
    // eslint-disable-next-line react-hooks/exhaustive-deps --  instead of blockState we are checking with blockIdMap, since blockState changes more frequently whereas blockIdMap changes only when the block is added or removed and that is actually when the block methods are needed to be recalculated
  }, [device, blockIdsFingerprint, getInterfaceStoreState, onAction, registry, mode]);

  const dataSourceMethods = useMemo(() => {
    if (mode === 'readonly') return {};

    const dataSources = getDataSourceRecordStoreState().dataSources;
    return getDataSourcesMethods({
      dataSourceIds: Object.keys(dataSources),
      onAction,
    });
  }, [getDataSourceRecordStoreState, onAction, mode]);

  const pageVariableMethods = useMemo((): Record<string, PageVariable> => {
    const activeInterfacePage = getActivePage(getInterfaceStoreState);
    const pageVariables = activeInterfacePage.properties.pageVariables;
    if (mode === 'readonly') return pageVariables ?? {};

    return getPageVariablesMethods({
      pageVariableIds: Object.keys(pageVariables ?? {}),
      onAction,
    });
  }, [getInterfaceStoreState, onAction, mode]);

  return { blockMethods, dataSourceMethods, pageVariableMethods };
}

/**
 * we created this hook that takes blocksRuntimeState in params because we observed that the context was causing
 * re-renders after the initial mount, which was leading to recalculation of the context, that was adding more overhead
 * to the page load time.
 */
function useComputeContextBuilder({
  blocksRuntimeState,
  mode,
}: {
  blocksRuntimeState?: Record<string, Record<string, unknown>>;
  mode: ComputeContextMode;
}) {
  const { getGlobalStateStoreState } = useGetGlobalStateStoreState();
  const { getInterfaceStoreState } = useGetInterfaceStoreState();
  // const { getWalkthroughComputeContext } = useGetWalkthroughComputeContext();
  const { blockMethods, dataSourceMethods, pageVariableMethods } = useInterfaceEntityMethods({
    mode,
  });
  const lastContextRef = useRef<ComputeContextType>(undefined);
  const lastArgs = useRef<Parameters<typeof buildComputeContextFromState>[0]>(undefined);

  const getComputeContextBuilder = useCallback<GetComputeContextBuilder>(() => {
    const globalState = getGlobalStateStoreState();
    const interfaceState = getInterfaceStoreState();
    // const walkthrough = getWalkthroughComputeContext();
    if (
      lastArgs.current?.interfaceRecord !== interfaceState.interfaceRecord ||
      lastArgs.current.blocksRuntimeState !== blocksRuntimeState ||
      lastArgs.current.blockMethods !== blockMethods ||
      lastArgs.current.dataSourceMethods !== dataSourceMethods ||
      lastArgs.current.pageVariableMethods !== pageVariableMethods ||
      // isEqual is needed for walkthrough as new reference is created on every render
      // !isEqual(lastArgs.current.walkthrough, walkthrough) ||
      lastArgs.current.state !== globalState
    ) {
      lastArgs.current = {
        state: globalState,
        interfaceRecord: interfaceState.interfaceRecord,
        blocksRuntimeState,
        blockMethods,
        // walkthrough,
        dataSourceMethods,
        pageVariableMethods,
      };
      lastContextRef.current = buildComputeContextFromState(lastArgs.current);
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- not null check
    return lastContextRef.current!;
  }, [
    // getWalkthroughComputeContext,
    getGlobalStateStoreState,
    getInterfaceStoreState,
    blocksRuntimeState,
    blockMethods,
    dataSourceMethods,
    pageVariableMethods,
  ]);

  return {
    getComputeContextBuilder,
  };
}

export function useGetComputeContext({
  mode = 'readonly',
}: {
  mode?: ComputeContextMode;
} = {}) {
  const { data: blocksRuntimeState } = useBlockRuntimeData();
  const { getComputeContextBuilder } = useComputeContextBuilder({
    blocksRuntimeState,
    mode,
  });

  return { getComputeContext: getComputeContextBuilder };
}

export function useReactiveComputeContext({
  mode = 'readonly',
}: {
  mode?: ComputeContextMode;
} = {}) {
  const { data: blocksRuntimeState } = useBlockRuntimeData();
  const blocksState = useGlobalStateStore().use.blocks();
  const interfaceRecord = useInterfaceStore().use.record.details();
  const blockRefs = useGlobalStateStore().use.blockRefs();
  const dataSourcesState = useGlobalStateStore().use.dataSources();
  const dataSourcesMeta = useGlobalStateStore().use.dataSourcesMeta();
  const pageInputs = useGlobalStateStore().use.pageInputs();
  const location = useGlobalStateStore().use.location();
  const userContext = useGlobalStateStore().use.userContext();
  const pageVariables = useGlobalStateStore().use.pageVariables();
  const pageFunctions = useGlobalStateStore().use.pageFunctions();
  const interfaceSessionStorage = useGlobalStateStore().use.interfaceSessionStorage();
  // const { walkthroughContext } = useWalkthroughComputeContext();
  const envVariables = useGlobalStateStore().use.envVariables();
  const lastContextRef = useRef<ComputeContextType>(undefined);
  const lastArgs = useRef<Parameters<typeof buildComputeContextFromState>[0]>(undefined);

  // this is extremely important to be memoized and done outside the buildComputeContextFromState
  // keeping it inside causes +~500ms of more JS execution time on page load in employee portal of JFL
  // NOTE: So, all the code changes here has to be battle tested to ensure that this is not causing any performance issues or unintended regressions
  const { blockMethods, dataSourceMethods, pageVariableMethods } = useInterfaceEntityMethods({
    mode,
  });

  const context = useMemo<ComputeContextType>(() => {
    if (
      /* eslint-disable react-compiler/react-compiler -- needed */
      lastArgs.current?.interfaceRecord !== interfaceRecord ||
      lastArgs.current.blocksRuntimeState !== blocksRuntimeState ||
      lastArgs.current.blockMethods !== blockMethods ||
      lastArgs.current.dataSourceMethods !== dataSourceMethods ||
      lastArgs.current.pageVariableMethods !== pageVariableMethods ||
      lastArgs.current.state.blocks !== blocksState ||
      lastArgs.current.state.blockRefs !== blockRefs ||
      lastArgs.current.state.dataSources !== dataSourcesState ||
      lastArgs.current.state.dataSourcesMeta !== dataSourcesMeta ||
      lastArgs.current.state.pageInputs !== pageInputs ||
      lastArgs.current.state.location !== location ||
      lastArgs.current.state.userContext !== userContext ||
      lastArgs.current.state.pageVariables !== pageVariables ||
      lastArgs.current.state.pageFunctions !== pageFunctions ||
      lastArgs.current.state.interfaceSessionStorage !== interfaceSessionStorage ||
      lastArgs.current.state.envVariables !== envVariables
      // isEqual is needed for walkthrough as new reference is created on every render
      // !isEqual(lastArgs.current.walkthrough, walkthrough)
    ) {
      lastArgs.current = {
        state: {
          blocks: blocksState,
          blockRefs,
          dataSources: dataSourcesState,
          dataSourcesMeta,
          pageInputs,
          location,
          userContext,
          pageVariables,
          pageFunctions,
          interfaceSessionStorage,
          envVariables,
        },
        interfaceRecord,
        blocksRuntimeState,
        blockMethods,
        dataSourceMethods,
        pageVariableMethods,
        // walkthrough: walkthroughContext,
      };

      lastContextRef.current = buildComputeContextFromState(lastArgs.current);
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- not null check
    return lastContextRef.current!;
    /* eslint-enable react-compiler/react-compiler -- needed */
  }, [
    blocksState,
    blockRefs,
    dataSourcesState,
    dataSourcesMeta,
    pageInputs,
    location,
    userContext,
    pageVariables,
    pageFunctions,
    interfaceSessionStorage,
    interfaceRecord,
    blocksRuntimeState,
    blockMethods,
    dataSourceMethods,
    pageVariableMethods,
    envVariables,
  ]);

  return { context };
}

// entityId you want to subscribe to, it should be present in the deps graph, for example can be blockId, dataSourceId,
// pageFuncId, etc.
// keys is used when u want to subscribe to an entity but from different locations
// key is used in case of repeatable like block where the entity id is constant but key will be changed based on the data length
// for example entityId: b_9kisj, key: runtimeBlock_b_9kisj_0
export function useNonReactiveComputeContext({
  entityId,
  key,
  blocksRuntimeState,
}: {
  entityId: string | null;
  key?: string;
  blocksRuntimeState?: Record<string, Record<string, unknown>>;
}) {
  const serverSnapshotRef = useRef<{
    context?: Record<string, unknown>;
    dependsOn?: DependsOnType[];
  }>(EMPTY_OBJECT);
  const snapshotRef = useRef<{
    context?: Record<string, unknown>;
    dependsOn?: DependsOnType[];
  }>(EMPTY_OBJECT);

  const { getComputeContextBuilder } = useComputeContextBuilder({
    blocksRuntimeState,
    mode: 'readonly',
  });

  const { graphManager, subscriptionManager } = useDependencyGraphProvider();
  const { subscribeToDependencyUpdates } = useGetSubscribeToDependencyUpdates();
  const { getNoCodeComputedData } = useGetNoCodeComputedData();

  const getSnapshot = useCallback(() => {
    let lastDependsOn = snapshotRef.current.dependsOn;
    let prevContext = snapshotRef.current.context;

    const dependsOn = graphManager.getNode(entityId)?.dependsOn;

    // if no dependsOn, then no need to subscribe
    if (!dependsOn?.length) return;

    // first check if the dependency item is changed, then rerender
    if (!isEqual(dependsOn, lastDependsOn)) {
      lastDependsOn = dependsOn;
    }

    const latestContext = getComputeContextBuilder();

    const { isContextChanged, nextContext } = getIsDependentContextChanged({
      prevContext,
      context: latestContext,
      dependsOn,
      notifyingUpdateForId: subscriptionManager.getNotifyingUpdateForId(),
      getNoCodeComputedData,
    });

    if (isContextChanged) {
      prevContext = nextContext;
    }

    const shouldReRender =
      lastDependsOn !== snapshotRef.current.dependsOn ||
      prevContext !== snapshotRef.current.context;

    if (shouldReRender) {
      snapshotRef.current = {
        context: prevContext,
        dependsOn,
      };
    }

    return snapshotRef.current;
  }, [
    graphManager,
    entityId,
    getComputeContextBuilder,
    subscriptionManager,
    getNoCodeComputedData,
  ]);

  const subscribe = useCallback(
    (callback: () => void) => {
      if (!entityId) {
        return () => {
          // noop
        };
      }

      // subscribe to dependency updates for the given entityIds
      const unsubscribe = subscribeToDependencyUpdates({
        id: entityId,
        callback,
        key,
      });

      return () => {
        unsubscribe();
      };
    },
    [entityId, key, subscribeToDependencyUpdates],
  );

  const getServerSnapshot = useCallback(() => {
    if (!entityId) {
      return;
    }

    const dependsOn = graphManager.getNode(entityId)?.dependsOn;

    if (!dependsOn?.length) {
      return;
    }

    const latestContext = getComputeContextBuilder();

    const { isContextChanged, nextContext } = getIsDependentContextChanged({
      prevContext: undefined, // no previous context on the server
      context: latestContext,
      dependsOn,
      notifyingUpdateForId: subscriptionManager.getNotifyingUpdateForId(),
      getNoCodeComputedData,
    });

    const shouldReRender =
      dependsOn !== serverSnapshotRef.current.dependsOn ||
      nextContext !== serverSnapshotRef.current.context;

    if (isContextChanged && shouldReRender) {
      serverSnapshotRef.current = {
        context: nextContext,
        dependsOn,
      };
    }

    return serverSnapshotRef.current;
  }, [
    entityId,
    graphManager,
    getComputeContextBuilder,
    subscriptionManager,
    getNoCodeComputedData,
  ]);

  useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // we are calling getComputeContext here in the render cycle because where the block state changes, we need to recompute the context, with updated
  // block state. because we only track against dynamic property paths, which handles scenarios of using external entities.
  return { context: getComputeContextBuilder() };
}
